import React, { useState } from 'react';
import { AutoLinkNode, LinkNode } from '@lexical/link';
import { ListItemNode, ListNode } from '@lexical/list';
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { ENGLISH } from '@/helpers/constants';
import styles from './CustomRichText.module.scss';
import HTMLImporter from './plugins/HTMLImporter';
import OnChangeHTMLPlugin from './plugins/OnChangeHTMLPlugin';
import ToolbarPlugin from './plugins/ToolbarPlugin';
import Spinner from '../UI/Spinner/Spinner';

interface CustomProps {
  id: string;
  language: string;
  handleChange: (value: string) => void;
  handleOpenVariables?: () => void;
  initialValue?: string | { EN: string; DE: string };
  variableSelected?: string;
  title?: string;
  placeholder?: string;
}

const CustomRichText = (props: CustomProps) => {
  const { id, language, handleChange, handleOpenVariables, variableSelected, initialValue, title, placeholder } = props;
  const [isReady, setIsReady] = useState(false);
  const initialConfig = {
    namespace: `myeditor-${id}`,
    theme: {
      text: {
        underline: 'underline',
      },
      list: {
        ul: 'editor-ul',
        ol: 'editor-ol',
        listitem: 'editor-listitem',
        nested: {
          listitem: 'editor-nested-listitem',
        },
      },
      link: 'editor-link',
    },
    onError: (error) => { throw error; },
    nodes: [HeadingNode, QuoteNode, ListNode, ListItemNode, LinkNode, AutoLinkNode],
  };

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <div className={styles.EditorContainer} id={id}>
        {!isReady && (
          <div className={styles.EditorLoaderOverlay}>
            <div className={styles.EditorLoader}><Spinner isVisible /> </div>
          </div>
        )}
        <ToolbarPlugin handleOpenVariables={handleOpenVariables} variableSelected={variableSelected} title={title} />
        <div className={styles.EditorInner}>
          <RichTextPlugin
            contentEditable={<ContentEditable className={styles.EditorInput} />}
            placeholder={placeholder ? <span>{placeholder}</span> : undefined}
            ErrorBoundary={LexicalErrorBoundary}
          />
          <OnChangeHTMLPlugin onChange={handleChange} />
          <HistoryPlugin externalHistoryState={undefined} />
          <HTMLImporter initialHTML={initialValue} onReady={() => setIsReady(true)} />
          <ListPlugin />
          <LinkPlugin />
          {language === ENGLISH.toLowerCase() && <AutoFocusPlugin />}
        </div>
      </div>
    </LexicalComposer>
  );
};

export default CustomRichText;
